import React, { useState, useRef, useEffect, useCallback } from "react";
import { Mic, Flag, Volume2, VolumeX, User, Bot, Clock, Lightbulb, Send } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import usePreventBack from "../hooks/usePreventBack";

const FONT_LINKS = (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');
  `}</style>
);

const ANSWER_ENDPOINT = "https://codematrix.co.in/auth/api/users/api/mock-interview/answer";

// How long (ms) of silence after speech before we treat the utterance as
// "done" and auto-submit it — this is what lets the user just talk with
// no button to press.
const SILENCE_MS = 3000;
// Ignore very short blips (e.g. a stray "um") shorter than this.
const MIN_ANSWER_CHARS = 2;

const INTERVIEW_SECONDS = 15 * 60;

// Quick, glanceable prompts shown in the left-hand "Interview Tips" panel.
const INTERVIEW_TIPS = [
  "Speak clearly and at a steady, natural pace.",
  "Keep answers focused — aim for about 1–2 minutes.",
  "Use specific examples from your own experience.",
  "It's okay to pause briefly to gather your thoughts.",
  "Just talk — your answer is captured and sent automatically.",
];

// Rough words-per-minute used only to give the "answer length" meter a
// sense of scale. This is not meant to be precise, just a visual cue that
// nudges answers toward the ~1-2 minute sweet spot.
const WORDS_PER_MINUTE = 130;
const TARGET_MIN_WORDS = WORDS_PER_MINUTE * 1; // ~1 minute
const TARGET_MAX_WORDS = WORDS_PER_MINUTE * 2; // ~2 minutes

// ---------------------------------------------------------------------------
function readStoredSessionId() {
  try {
    return localStorage.getItem("mockInterviewSessionId") || "";
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
async function sendAnswer(sessionIdArg, answer) {
  const form = new FormData();
  const sessionId = sessionIdArg || localStorage.getItem("mockInterviewSessionId");
  form.append("sessionId", sessionId);
  form.append("answer", answer);
  const token = localStorage.getItem("token");

  const res = await fetch(ANSWER_ENDPOINT, {
    method: "POST",
    headers: {
      Authorization: `${token}`,
    },
    body: form,
  });

  let data = null;
  try {
    data = await res.text();
  } catch {
    // non-JSON response — fall through, data stays null
  }

  if (!res.ok) {
    const errMsg = data?.message || data?.error || `Request failed (${res.status})`;
    throw new Error(errMsg);
  }

  return data;
}

function extractReplyText(data) {
  if (data == null) return "(empty response)";
  if (typeof data === "string") return data;
  const nested = data.data || {};
  const text =
    data.answer ||
    data.reply ||
    data.message ||
    data.question ||
    data.nextQuestion ||
    data.feedback ||
    nested.answer ||
    nested.reply ||
    nested.message ||
    nested.question ||
    nested.nextQuestion ||
    nested.feedback;
  return text || JSON.stringify(data, null, 2);
}

function useSpeechRecognition(initialLang = "en-IN") {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [supported, setSupported] = useState(true);
  const [permissionError, setPermissionError] = useState(false);

  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const finalRef = useRef(""); // accumulated final transcript for the current answer
  // Mirrors the `interim` state but as a ref, so it's always current no
  // matter which render's closure is reading it (see getFullText below).
  const interimRef = useRef("");
  const lastActivityRef = useRef(Date.now());
  const langRef = useRef(initialLang);

  useEffect(() => {
    const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

    if (!SR) {
      setSupported(false);
      return;
    }

    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = langRef.current;
    rec.maxAlternatives = 3;

    rec.onresult = (event) => {

      if (!shouldListenRef.current) return;

      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        // Pick the highest-confidence alternative instead of always [0] —
        // with maxAlternatives = 3, [0] isn't always the best guess.
        let best = result[0];
        for (let j = 1; j < result.length; j++) {
          if (result[j].confidence > best.confidence) best = result[j];
        }
        if (result.isFinal) {
          finalRef.current = (finalRef.current + " " + best.transcript).trim();
        } else {
          interimText += best.transcript;
        }
      }
      setInterim(interimText);
      interimRef.current = interimText;
      lastActivityRef.current = Date.now();
    };

    rec.onstart = () => {
      setListening(true);
      setPermissionError(false);

      lastActivityRef.current = Date.now();
    };

    rec.onend = () => {
      setListening(false);

      if (shouldListenRef.current) {
        if (interimRef.current) {
          finalRef.current = (finalRef.current + " " + interimRef.current).trim();
          interimRef.current = "";
          setInterim("");
        }
        try {
          rec.start();
        } catch {
          // already starting / transient — ignore
        }
      }
    };

    rec.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setPermissionError(true);
        shouldListenRef.current = false;
        setListening(false);
        return;
      }
      // "no-speech", "network", "aborted" etc. are recoverable — onend
      // will fire right after this and handle the restart.
    };

    recognitionRef.current = rec;

    return () => {
      shouldListenRef.current = false;
      recognitionRef.current = null;
      try {
        rec.onend = null; // don't let the teardown trigger an auto-restart
        rec.stop();
      } catch {
        // ignore
      }
    };
  }, []);

  const setLang = useCallback((lang) => {
    if (!lang || lang === langRef.current) return;
    langRef.current = lang;
    if (recognitionRef.current) {
      recognitionRef.current.lang = lang;
      // Changing .lang on a live recognizer doesn't reliably take effect
      // mid-session in most browsers, so if we're actively listening,
      // restart it to pick up the new language cleanly.
      if (shouldListenRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // onend will restart with the new lang
        }
      }
    }
  }, []);

  const start = useCallback(() => {
    if (!recognitionRef.current) return;
    shouldListenRef.current = true;
    finalRef.current = "";
    interimRef.current = "";
    setInterim("");
    lastActivityRef.current = Date.now();
    try {
      recognitionRef.current.start();
    } catch {
      // already running — fine, ignore InvalidStateError
    }
  }, []);

  const stop = useCallback(() => {
    shouldListenRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // ignore
      }
    }
  }, []);


  const getFullText = useCallback(() => {
    return (finalRef.current + " " + interimRef.current).trim();
  }, []);

  return {
    listening,
    interim,
    supported,
    permissionError,
    start,
    stop,
    getFullText,
    finalRef,
    lastActivityRef,
    setLang,
  };
}


let voicesLoadPromise = null;
function getVoicesAsync() {
  if (!window.speechSynthesis) return Promise.resolve([]);
  const existing = window.speechSynthesis.getVoices();
  if (existing.length) return Promise.resolve(existing);
  if (voicesLoadPromise) return voicesLoadPromise;
  voicesLoadPromise = new Promise((resolve) => {
    const handle = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length) {
        window.speechSynthesis.removeEventListener("voiceschanged", handle);
        resolve(voices);
      }
    };
    window.speechSynthesis.addEventListener("voiceschanged", handle);
    // Safety net in case the browser never fires voiceschanged.
    setTimeout(() => resolve(window.speechSynthesis.getVoices()), 1200);
  });
  return voicesLoadPromise;
}

function detectLangCode(text) {
  return /[\u0900-\u097F]/.test(text) ? "hi-IN" : "en-US";
}

function pickVoice(voices, langCode = "en-US") {
  if (!voices || voices.length === 0) return null;
  const prefix = langCode.split("-")[0]; // "hi" or "en"
  const langVoices = voices.filter((v) => new RegExp(`^${prefix}([-_]|$)`, "i").test(v.lang));
  const pool = langVoices.length ? langVoices : voices; // fallback if no matching voice installed
  const femaleHints =
    /female|zira|samantha|victoria|karen|moira|tessa|susan|google us english|google.*hindi|aria|swara|heera|lekha/i;
  return pool.find((v) => femaleHints.test(v.name)) || pool[0];
}

async function speak(text, enabled, setSpeaking, onEnd) {
  if (!enabled || !window.speechSynthesis) {
    onEnd?.();
    return;
  }
  window.speechSynthesis.cancel();

  const langCode = detectLangCode(text);
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = langCode;
  utter.rate = 0.95;
  utter.pitch = 1;

  const voices = await getVoicesAsync();
  const preferred =
    voices.find((v) => v.lang === langCode && /swara|heera|natural/i.test(v.name)) ||
    pickVoice(voices, langCode);
  if (preferred) utter.voice = preferred;

  utter.onstart = () => setSpeaking(true);
  utter.onend = () => {
    setSpeaking(false);
    onEnd?.(langCode);
  };
  utter.onerror = () => {
    setSpeaking(false);
    onEnd?.(langCode);
  };
  window.speechSynthesis.speak(utter);
}

function formatTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function MockInterviewChat() {
  const navigate = useNavigate();
  usePreventBack();
  const location = useLocation();
  const [sessionId, setSessionId] = useState("");
  const [voiceOut, setVoiceOut] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [secondsLeft, setSecondsLeft] = useState(INTERVIEW_SECONDS);
  const [finished, setFinished] = useState(false);
  const [tabSwitchError, setTabSwitchError] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const scrollRef = useRef(null);
  const seededRef = useRef(false); // guards against re-seeding on re-renders
  const submittingRef = useRef(false);
  const finishedRef = useRef(false);
  // Always mirrors the latest sessionId so async callbacks (submitMessage)
  // never read a stale closed-over value.
  const sessionIdRef = useRef("");

  const {
    listening,
    interim,
    supported,
    permissionError,
    start,
    stop,
    getFullText,
    finalRef,
    lastActivityRef,
    setLang,
  } = useSpeechRecognition();

  useEffect(() => {
    const stored = readStoredSessionId();
    setSessionId(stored);
    sessionIdRef.current = stored;
  }, []);

  function CenterWarningToast({ warnings }) {
    return (
      <div style={styles.warningOverlay}>
        {warnings.map((w) => (
          <div key={w.id} style={styles.warningToast}>
            {w.message}
          </div>
        ))}
      </div>
    );
  }

  const [warnings, setWarnings] = useState([]);
  const warningCooldownRef = useRef({}); // message -> last shown timestamp

  const pushWarning = useCallback((message, cooldownMs = 6000) => {
    const now = Date.now();
    const last = warningCooldownRef.current[message] || 0;
    if (now - last < cooldownMs) return; // skip if we just showed this one
    warningCooldownRef.current[message] = now;

    const id = now + Math.random();
    setWarnings((w) => [...w, { id, message }]);
    setTimeout(() => {
      setWarnings((w) => w.filter((x) => x.id !== id));
    }, 3000);
  }, []);

  // Given the AI's text, switch the recognizer's language to match before
  // we start listening for the reply — so a Hindi question gets Hindi
  // speech recognition for the candidate's answer, and vice versa.
  const startListeningFor = useCallback(
    (aiText) => {
      if (finishedRef.current) return;
      const langCode = detectLangCode(aiText);
      setLang(langCode === "hi-IN" ? "hi-IN" : "en-IN");
      start();
    },
    [setLang, start]
  );


  useEffect(() => {
    if (seededRef.current) return;

    const rawFirstMessage = location.state?.message;

    if (!rawFirstMessage) return;

    const firstQuestion = extractReplyText(rawFirstMessage);
    seededRef.current = true;

    setMessages((m) => (m.length === 0 ? [{ role: "assistant", content: firstQuestion }] : m));
    speak(firstQuestion, voiceOut, setSpeaking, () => {
      startListeningFor(firstQuestion);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // If speech output is off (or there's no seeded first message), still
  // start listening automatically so the user never has to press anything.
  useEffect(() => {
    if (supported && !finished && !seededRef.current) {
      start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, interim, thinking]);

  // ------------------------------------------------------------------
  // 15-minute countdown timer. Auto-finishes the interview at zero.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (finished) return;
    if (secondsLeft <= 0) {
      handleFinish(true);
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, finished]);


  useEffect(() => {
    if (finished) return;
    const poll = setInterval(() => {
      if (!listening || submittingRef.current) return;
      const text = getFullText();
      if (text.length < MIN_ANSWER_CHARS) return;
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= SILENCE_MS) {
        submittingRef.current = true;
        submitMessage(text);
      }
    }, 300);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening, finished]);

  // ------------------------------------------------------------------
  // Tab-switch detection — if the candidate switches tabs, minimizes the
  // window, or otherwise navigates away during the interview, end it
  // immediately and flag it as a tab-switch violation.
  // ------------------------------------------------------------------
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && !finishedRef.current) {
        setTabSwitchError(true);
        pushWarning("Tab switching is not allowed");
        handleFinish(false, true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitMessage = async (text) => {
    const content = text.trim();
    if (!content) {
      submittingRef.current = false;
      return;
    }

    let sid = sessionIdRef.current || sessionId.trim();
    if (!sid) {
      sid = readStoredSessionId();
      if (sid) {
        sessionIdRef.current = sid;
        setSessionId(sid);
      }
    }

    if (!sid) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "No session ID set — add one above (from localStorage or pasted manually) before sending." },
      ]);
      submittingRef.current = false;
      return;
    }

    stop();
    finalRef.current = "";
    setManualInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setThinking(true);

    try {
      const data = await sendAnswer(sid, content);
      const replyText = extractReplyText(data);
      setMessages((m) => [...m, { role: "assistant", content: replyText }]);
      setThinking(false);
      submittingRef.current = false;

      speak(replyText, voiceOut, setSpeaking, () => {
        startListeningFor(replyText);
      });
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Sorry, that request failed (${e.message}). Try again.` },
      ]);
      setThinking(false);
      submittingRef.current = false;
      if (!finishedRef.current) start();
    }
  };

  // Manual "type instead of / as well as speaking" send path — shares the
  // exact same submitMessage() function (and therefore the same session-id
  // handling) as the voice auto-submit above.
  const handleManualSend = () => {
    const text = manualInput.trim();
    if (!text || thinking || submittingRef.current) return;
    submittingRef.current = true;
    submitMessage(text);
  };

  const handleFinish = (auto = false, tabSwitch = false) => {
    if (finishedRef.current) return;

    finishedRef.current = true;
    setFinished(true);
    stop();
    window.speechSynthesis?.cancel();

    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        content: tabSwitch
          ? "Interview ended: tab switching is not allowed during the interview."
          : auto
          ? "Time's up! The interview has ended. Thanks for your responses."
          : "Interview ended. Thanks for your responses!",
      },
    ]);

    // Navigate after a short delay so the state updates complete
    setTimeout(() => {
      navigate("/mockreport", {
        state: {
          sessionId,
          autoFinished: auto,
          tabSwitchViolation: tabSwitch,
        },
      });
    }, 1000);
  };

  const liveText = getFullText();
  const timeLow = secondsLeft <= 60;

  return (
    <div style={styles.appWrap}>
      {FONT_LINKS}
      <CenterWarningToast warnings={warnings} />
      <div style={styles.bgGlow} />

      {/* ---------------------------------------------------------- */}
      {/* Top bar: brand, timer, finish                              */}
      {/* ---------------------------------------------------------- */}
      <header style={styles.header}>
        <div style={styles.brandRow}>
          <img src="codematrixLogo.png" alt="CodeMatrix Logo" style={{ width: 100, height: 100 }} />
          <div>
            <div style={styles.brandName}>Mock Interview</div>
            <div style={styles.brandTagline}>AI interview practice &amp; personality development</div>
          </div>
        </div>
        <div style={styles.headerControls}>
          <div style={{ ...styles.timerBadge, ...(timeLow ? styles.timerBadgeLow : {}) }}>
            <Clock size={14} />
            {formatTime(secondsLeft)}
          </div>
          <button
            style={styles.finishBtn}
            onClick={() => handleFinish(false)}
            disabled={finished}
            aria-label="Finish interview"
          >
            <Flag size={14} />
            Finish
          </button>
        </div>
      </header>


      <div style={styles.mainRow}>
        <TipsPanel />

        <div style={styles.rightColumn}>
          <div style={styles.stage}>
            <div style={styles.stagePanel}>
              <CameraTile finished={finished} />
            </div>

            <div style={styles.stageDivider} />

            <div style={styles.stagePanel}>
              <InterviewerTile speaking={speaking} thinking={thinking} listening={listening} />
            </div>
          </div>

          {/* ---------------------------------------------------------- */}
          {/* Message tray: conversation + live transcript / controls    */}
          {/* ---------------------------------------------------------- */}
          <div style={styles.bottomBar}>
            <div ref={scrollRef} style={styles.bottomScroll}>
              {messages.length === 0 && !thinking && (
                <div style={styles.emptyState}>Just start speaking — your answer is captured and sent automatically.</div>
              )}
              {messages.map((m, i) => (
                <ChatBubble key={i} role={m.role} content={m.content} />
              ))}
              {thinking && <ChatBubble role="assistant" content="" thinking />}

              {/* Fills the otherwise-blank space below the conversation with
                  a live "answer in progress" status so the panel never
                  looks idle while the user is talking or the mic is open. */}
              {!finished && !thinking && messages.length > 0 && (
                <AnswerStatusCard listening={listening} liveText={liveText} />
              )}
            </div>

            {tabSwitchError && (
              <div style={styles.tabSwitchWarnBox}>
                ⚠ Tab switching is not allowed during the interview. Your interview has been ended.
              </div>
            )}

            {permissionError && (
              <div style={styles.warnBoxInline}>
                Microphone access was blocked. Please allow microphone access in your browser, then{" "}
                <button style={styles.inlineRetryBtn} onClick={start}>
                  tap here to retry
                </button>
                .
              </div>
            )}

            {!supported && (
              <div style={styles.warnBoxInline}>
                Speech recognition isn't supported in this browser. Try Chrome or Edge to answer by voice.
              </div>
            )}

            {finished ? (
              <div style={styles.finishedBox}>Interview finished.</div>
            ) : (
              <div style={styles.transcriptRow}>
                <div style={{ ...styles.micIndicator, ...(listening ? styles.micIndicatorActive : {}) }}>
                  <Mic size={14} color={listening ? "#2F8F5B" : "#9A94A6"} />
                </div>
                <div style={styles.transcriptBox}>
                  <div style={styles.transcriptLabel}>
                    {thinking ? "Thinking…" : listening ? "Listening…" : "Getting ready…"}
                  </div>
                  <div style={styles.transcriptText}>
                    {liveText || <span style={{ color: "#A6A0B4" }}>Speak whenever you're ready…</span>}
                  </div>
                  {/* Manual typed-answer input — same submit path/session
                      handling as voice, so it works even mid-pause. */}
                  <div style={styles.manualInputRow}>
                    <input
                      type="text"
                      value={manualInput}
                      onChange={(e) => setManualInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleManualSend();
                        }
                      }}
                      placeholder="…or type your answer here"
                      style={styles.manualInput}
                      disabled={thinking}
                    />
                    <button
                      style={{
                        ...styles.manualSendBtn,
                        ...(manualInput.trim() && !thinking ? {} : styles.manualSendBtnDisabled),
                      }}
                      onClick={handleManualSend}
                      disabled={thinking || !manualInput.trim()}
                      aria-label="Send typed answer"
                    >
                      <Send size={14} />
                    </button>
                  </div>
                </div>
                <button style={styles.ghostBtn} onClick={() => setVoiceOut((v) => !v)}>
                  {voiceOut ? <Volume2 size={16} /> : <VolumeX size={16} />}
                  {speaking && <span style={styles.speakingDot} />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left panel — quick interview tips for the candidate
// ---------------------------------------------------------------------------
function TipsPanel() {
  return (
    <div style={styles.tipsPanel}>
      <div style={styles.tipsHeaderRow}>
        <div style={styles.tipsIconBadge}>
          <Lightbulb size={15} color="#4F6BFF" />
        </div>
        <div>
          <div style={styles.tipsHeader}>Interview Tips</div>
          <div style={styles.tipsSubheader}>Keep these in mind</div>
        </div>
      </div>

      <ul style={styles.tipsList}>
        {INTERVIEW_TIPS.map((tip, i) => (
          <li key={i} style={styles.tipsItem}>
            <span style={styles.tipsNumber}>{String(i + 1).padStart(2, "0")}</span>
            <span style={styles.tipsItemText}>{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


function AnswerStatusCard({ listening, liveText }) {
  const wordCount = liveText ? liveText.trim().split(/\s+/).filter(Boolean).length : 0;
  const progressPct = Math.min(100, Math.round((wordCount / TARGET_MAX_WORDS) * 100));
  const inSweetSpot = wordCount >= TARGET_MIN_WORDS && wordCount <= TARGET_MAX_WORDS;

  return (
    <div style={styles.statusCard}>
      <div style={styles.statusDivider}>
        <span style={styles.statusDividerLine} />
      </div>

      <div style={styles.statusRow}>
        <div style={{ ...styles.statusMicBadge, ...(listening ? styles.statusMicBadgeActive : {}) }}>
          <Mic size={16} color={listening ? "#2F8F5B" : "#A79FBC"} />
          {listening && <MiniWave />}
        </div>
        <div style={styles.statusTextCol}>
          <div style={styles.statusHeadline}>{listening ? "Listening…" : "Mic is off"}</div>
          <div style={styles.statusSubline}>
            {listening
              ? "AI is waiting for your answer. It's captured and sent automatically."
              : "Your answer will be sent automatically once the mic reopens."}
          </div>
        </div>
      </div>

      <div style={styles.statusMeterBlock}>
        <div style={styles.statusMeterLabelRow}>
          <span style={styles.statusMeterLabel}>Answer length</span>
          <span style={{ ...styles.statusMeterLabel, color: inSweetSpot ? "#2F8F5B" : "#A79FBC" }}>
            Target: 1–2 min
          </span>
        </div>
        <div style={styles.statusMeterTrack}>
          <div
            style={{
              ...styles.statusMeterFill,
              width: `${progressPct}%`,
              background: inSweetSpot ? "#2F8F5B" : "#4F6BFF",
            }}
          />
        </div>
      </div>
    </div>
  );
}

// Small three-bar waveform used inside the mic badge to signal live audio.
function MiniWave() {
  return (
    <div style={styles.miniWaveWrap} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span key={i} style={{ ...styles.miniWaveBar, animationDelay: `${i * 0.15}s` }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left panel — the candidate's live camera feed
// ---------------------------------------------------------------------------
function CameraTile({ finished }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices
      ?.getUserMedia({ video: true, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch((err) => console.error("Camera access failed:", err));

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (finished && streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    }
  }, [finished]);

  return (
    <div style={styles.cameraTile}>
      <video ref={videoRef} autoPlay playsInline muted style={styles.video} />
      <div style={styles.liveBadge}>
        <span style={styles.liveDot} />
        LIVE
      </div>
      <div style={styles.nameplate}>You</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small animated equalizer bars shown near the interviewer while it talks.
// ---------------------------------------------------------------------------
function SpeakingWave({ active }) {
  return (
    <div style={{ ...styles.waveWrap, opacity: active ? 1 : 0 }} aria-hidden={!active}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          style={{
            ...styles.waveBar,
            animationPlayState: active ? "running" : "paused",
            animationDelay: `${i * 0.12}s`,
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right panel — the AI interviewer avatar
// ---------------------------------------------------------------------------
function InterviewerTile({ speaking, thinking, listening }) {
  const [mouth, setMouth] = useState(0.12);
  const [blink, setBlink] = useState(false);
  const mouthTimerRef = useRef(null);
  const blinkTimerRef = useRef(null);

  useEffect(() => {
    if (speaking) {
      mouthTimerRef.current = setInterval(() => {
        setMouth(0.15 + Math.random() * 0.8);
      }, 100);
    } else {
      clearInterval(mouthTimerRef.current);
      setMouth(0.12);
    }
    return () => clearInterval(mouthTimerRef.current);
  }, [speaking]);

  useEffect(() => {
    blinkTimerRef.current = setInterval(() => {
      setBlink(true);
      setTimeout(() => setBlink(false), 140);
    }, 3200);
    return () => clearInterval(blinkTimerRef.current);
  }, []);

  const mouthH = 8 + mouth * 34;
  const mouthY = 158 - mouthH / 2;
  const eyeRy = blink ? 1.5 : 11;

  const statusColor = speaking ? "#D9622B" : thinking ? "#C79A3E" : "#2F8F5B";

  return (
    <div style={styles.interviewerTile}>
      <div
        style={{
          ...styles.avatarGlow,
          ...(speaking ? styles.avatarGlowSpeaking : {}),
          ...(listening && !speaking && !thinking ? styles.avatarGlowListening : {}),
        }}
      >
        <svg viewBox="0 0 240 240" width="100%" height="100%" style={{ display: "block" }}>
          <defs>
            <linearGradient id="faceGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2C2740" />
              <stop offset="100%" stopColor="#1B1826" />
            </linearGradient>
          </defs>
          <circle cx="120" cy="122" r="104" fill="url(#faceGrad)" />
          <ellipse cx="76" cy="104" rx="11" ry={eyeRy} fill="#F6F4EF" style={{ transition: "ry 0.09s ease" }} />
          <ellipse cx="164" cy="104" rx="11" ry={eyeRy} fill="#F6F4EF" style={{ transition: "ry 0.09s ease" }} />
          <rect
            x="82"
            y={mouthY}
            width="76"
            height={mouthH}
            rx={mouthH / 2}
            fill="#F0955C"
            style={{ transition: "height 0.09s ease, y 0.09s ease" }}
          />
        </svg>
      </div>

      <div style={styles.interviewerLabel}>Interviewer</div>

      <div style={styles.interviewerStatusRow}>
        {thinking ? (
          <span style={styles.thinkingDots}>
            Thinking
            <span style={styles.thinkingDot} />
            <span style={{ ...styles.thinkingDot, animationDelay: "0.15s" }} />
            <span style={{ ...styles.thinkingDot, animationDelay: "0.3s" }} />
          </span>
        ) : (
          <>
            <span
              style={{
                ...styles.avatarStatusDot,
                background: statusColor,
                ...(listening || speaking ? styles.avatarStatusDotPulsing : {}),
              }}
            />
            <span style={styles.avatarStatusText}>{speaking ? "Speaking" : listening ? "Listening" : "Standing by"}</span>
          </>
        )}
      </div>

      <SpeakingWave active={speaking} />
    </div>
  );
}

function ChatBubble({ role, content, thinking }) {
  const isAI = role === "assistant";
  return (
    <div style={{ ...styles.bubbleRow, justifyContent: isAI ? "flex-start" : "flex-end" }}>
      {isAI && (
        <div style={styles.avatar}>
          <Bot size={16} color="#FFFFFF" />
        </div>
      )}
      <div style={{ ...styles.bubble, ...(isAI ? styles.bubbleAI : styles.bubbleUser) }}>
        {thinking ? (
          <span style={styles.typingDots}>
            <span style={styles.dot} />
            <span style={{ ...styles.dot, animationDelay: "0.15s" }} />
            <span style={{ ...styles.dot, animationDelay: "0.3s" }} />
          </span>
        ) : (
          content
        )}
      </div>
      {!isAI && (
        <div style={{ ...styles.avatar, background: "#4F6BFF" }}>
          <User size={16} color="#FFFFFF" />
        </div>
      )}
    </div>
  );
}

const styles = {
  appWrap: {
    height: "100vh",
    width: "100%",
    background: "linear-gradient(180deg, #F3F5FB 0%, #EEF1F9 55%, #E9EDF7 100%)",
    fontFamily: "'Inter', sans-serif",
    color: "#211C2E",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  bgGlow: {
    position: "fixed",
    inset: 0,
    backgroundImage:
      "radial-gradient(circle at 18% 12%, rgba(79,107,255,0.07), transparent 46%), radial-gradient(circle at 84% 80%, rgba(78,139,124,0.06), transparent 50%)",
    pointerEvents: "none",
  },

  // ---------------- header ----------------
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 28px",
    position: "relative",
    zIndex: 2,
    flexShrink: 0,
  },
  brandRow: { display: "flex", alignItems: "center", gap: 10 },
  brandMark: {
    width: 32,
    height: 32,
    borderRadius: 9,
    background: "#4F6BFF",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Fraunces', serif",
    fontWeight: 700,
    fontSize: 13,
    boxShadow: "0 4px 10px rgba(79,107,255,0.28)",
  },
  brandName: { fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 18, letterSpacing: 0.2, color: "#211C2E" },
  brandTagline: { fontSize: 11.5, color: "#8B8398", marginTop: 1 },
  headerControls: { display: "flex", alignItems: "center", gap: 10 },
  timerBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 12px",
    borderRadius: 10,
    border: "1px solid rgba(33,28,46,0.08)",
    background: "#FFFFFF",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 13,
    fontWeight: 600,
    color: "#4B4459",
    boxShadow: "0 1px 2px rgba(33,28,46,0.04)",
  },
  timerBadgeLow: {
    border: "1px solid #E7B8A6",
    background: "#FBEDE7",
    color: "#B8476B",
  },
  finishBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    border: "none",
    background: "#B8476B",
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(184,71,107,0.28)",
  },

  // ---------------- main content: sidebar (tips) + right region (stage | chat) ----------------
  mainRow: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    minHeight: 0,
    gap: 16,
    padding: "4px 16px 12px",
    boxSizing: "border-box",
    position: "relative",
    zIndex: 1,
  },
  rightColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    minWidth: 0,
    minHeight: 0,
    gap: 16,
  },

  // ---------------- left panel: camera + interviewer stage ----------------
  stage: {
    flexShrink: 0,
    width: "36%",
    minWidth: 260,
    maxWidth: 400,
    alignSelf: "stretch",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 20,
    padding: "20px 12px",
    background: "#FFFFFF",
    borderRadius: 20,
    boxShadow: "0 1px 2px rgba(33,28,46,0.06), 0 14px 30px rgba(33,28,46,0.10)",
    boxSizing: "border-box",
  },
  stagePanel: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
  },
  stageDivider: {
    height: 1,
    width: "85%",
    background: "linear-gradient(to right, transparent, rgba(33,28,46,0.10), transparent)",
    flexShrink: 0,
  },

  // ---------------- left sidebar: interview tips (full height, standalone) ----------------
  tipsPanel: {
    width: 280,
    flexShrink: 0,
    alignSelf: "stretch",
    background: "#FFFFFF",
    borderRadius: 18,
    boxShadow: "0 1px 2px rgba(33,28,46,0.06), 0 14px 30px rgba(33,28,46,0.10)",
    display: "flex",
    flexDirection: "column",
    padding: "20px 18px",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  tipsHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 18,
    flexShrink: 0,
  },
  tipsIconBadge: {
    width: 30,
    height: 30,
    borderRadius: 9,
    background: "linear-gradient(160deg, #EDF0FF 0%, #E4E9FF 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  tipsHeader: {
    fontFamily: "'Fraunces', serif",
    fontWeight: 600,
    fontSize: 14.5,
    color: "#211C2E",
    lineHeight: 1.2,
  },
  tipsSubheader: {
    fontSize: 10.5,
    color: "#A79FBC",
    marginTop: 2,
    letterSpacing: 0.2,
  },
  tipsList: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    overflowY: "auto",
    minHeight: 0,
  },
  tipsItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 10px",
    borderRadius: 12,
    background: "#F8F8FC",
    border: "1px solid rgba(33,28,46,0.04)",
  },
  tipsNumber: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 10,
    fontWeight: 600,
    color: "#4F6BFF",
    background: "#EDF0FF",
    borderRadius: 6,
    padding: "2px 5px",
    lineHeight: 1.4,
    flexShrink: 0,
    marginTop: 1,
  },
  tipsItemText: {
    fontSize: 12,
    lineHeight: 1.5,
    color: "#4B4459",
  },

  cameraTile: {
    width: "min(100%, 260px)",
    aspectRatio: "1 / 1",
    borderRadius: 16,
    overflow: "hidden",
    position: "relative",
    background: "#1B1826",
    boxShadow: "0 1px 2px rgba(33,28,46,0.06), 0 10px 20px rgba(33,28,46,0.14)",
    margin: "0 auto",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transform: "scaleX(-1)",
  },
  liveBadge: {
    position: "absolute",
    top: 8,
    left: 8,
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 7px",
    borderRadius: 6,
    background: "rgba(20,17,28,0.55)",
    backdropFilter: "blur(6px)",
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: 0.5,
    color: "#FFFFFF",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  liveDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#EF5C7E",
    boxShadow: "0 0 0 2px rgba(239,92,126,0.3)",
    animation: "mi-pulse 1.4s infinite ease-in-out",
    display: "inline-block",
  },
  nameplate: {
    position: "absolute",
    bottom: 8,
    left: 8,
    padding: "3px 8px",
    borderRadius: 6,
    background: "rgba(20,17,28,0.55)",
    backdropFilter: "blur(6px)",
    fontSize: 10,
    fontWeight: 500,
    color: "#FFFFFF",
  },

  interviewerTile: {
    width: "min(100%, 260px)",
    aspectRatio: "1 / 1",
    borderRadius: 16,
    background: "#FFFFFF",
    border: "1px solid rgba(33,28,46,0.06)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "12px 10px",
    boxSizing: "border-box",
    margin: "0 auto",
  },
  avatarGlow: {
    width: "min(16vh, 130px)",
    height: "min(16vh, 130px)",
    borderRadius: "50%",
    padding: 4,
    border: "2px solid #EEF0FA",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    boxShadow: "0 6px 12px rgba(33,28,46,0.14)",
    flexShrink: 0,
  },
  avatarGlowSpeaking: {
    border: "2px solid #4F6BFF",
    boxShadow: "0 0 0 4px rgba(79,107,255,0.14), 0 6px 12px rgba(33,28,46,0.14)",
    animation: "mi-ring-pulse 1.4s infinite ease-in-out",
  },
  avatarGlowListening: {
    border: "2px solid #B7E4C8",
    boxShadow: "0 0 0 3px rgba(47,143,91,0.10), 0 6px 12px rgba(33,28,46,0.14)",
    animation: "mi-ring-breathe 2.6s infinite ease-in-out",
  },
  interviewerLabel: { fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 14, color: "#211C2E", lineHeight: 1 },
  interviewerStatusRow: { display: "flex", alignItems: "center", gap: 5, minHeight: 12 },
  avatarStatusDot: { width: 6, height: 6, borderRadius: "50%", transition: "background 0.2s ease", flexShrink: 0 },
  avatarStatusDotPulsing: {
    animation: "mi-pulse 1.2s infinite ease-in-out",
  },
  avatarStatusText: {
    fontSize: 10,
    fontFamily: "'IBM Plex Mono', monospace",
    color: "#7A7488",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    whiteSpace: "nowrap",
  },
  thinkingDots: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    fontSize: 10,
    fontFamily: "'IBM Plex Mono', monospace",
    color: "#C79A3E",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    whiteSpace: "nowrap",
  },
  thinkingDot: {
    width: 3,
    height: 3,
    borderRadius: "50%",
    background: "#C79A3E",
    display: "inline-block",
    animation: "mi-bounce 1s infinite ease-in-out",
  },

  waveWrap: {
    display: "flex",
    alignItems: "flex-end",
    gap: 2,
    height: 10,
    transition: "opacity 0.2s ease",
  },
  waveBar: {
    width: 2,
    height: 4,
    borderRadius: 1,
    background: "#4F6BFF",
    display: "inline-block",
    animation: "mi-wave 0.9s infinite ease-in-out",
  },

  bottomBar: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    background: "#FFFFFF",
    borderRadius: 20,
    boxShadow: "0 1px 2px rgba(33,28,46,0.06), 0 14px 30px rgba(33,28,46,0.10)",
    padding: "22px 26px 20px",
    position: "relative",
    zIndex: 2,
    overflow: "hidden",
    boxSizing: "border-box",
  },
  bottomScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    paddingBottom: 8,
  },
  emptyState: { fontSize: 14, color: "#A79FBC", textAlign: "center", padding: "4px 0" },
  bubbleRow: { display: "flex", gap: 10, alignItems: "flex-end" },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "#211C2E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  bubble: { maxWidth: "80%", padding: "13px 17px", borderRadius: 14, fontSize: 17, lineHeight: 1.6, whiteSpace: "pre-wrap" },
  bubbleAI: { background: "#F1EDFA", color: "#211C2E", borderTopLeftRadius: 4 },
  bubbleUser: { background: "#4F6BFF", color: "#FFFFFF", borderTopRightRadius: 4 },
  typingDots: { display: "flex", gap: 5, padding: "4px 0" },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#A79FBC",
    display: "inline-block",
    animation: "mi-bounce 1s infinite ease-in-out",
  },

  statusCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 16,
    padding: "18px 6px 8px",
  },
  statusDivider: { display: "flex", alignItems: "center" },
  statusDividerLine: {
    flex: 1,
    height: 1,
    background: "linear-gradient(to right, transparent, rgba(33,28,46,0.10), transparent)",
  },
  statusRow: { display: "flex", alignItems: "center", gap: 14 },
  statusMicBadge: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "#F5F4FA",
    border: "1px solid rgba(33,28,46,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    position: "relative",
    transition: "background 0.2s ease, border-color 0.2s ease",
  },
  statusMicBadgeActive: {
    background: "#E8F6EE",
    border: "1px solid #B7E4C8",
  },
  miniWaveWrap: {
    position: "absolute",
    bottom: -3,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "flex-end",
    gap: 2,
    height: 8,
  },
  miniWaveBar: {
    width: 2,
    height: 4,
    borderRadius: 1,
    background: "#2F8F5B",
    display: "inline-block",
    animation: "mi-wave 0.9s infinite ease-in-out",
  },
  statusTextCol: { display: "flex", flexDirection: "column", gap: 2 },
  statusHeadline: { fontSize: 13.5, fontWeight: 600, color: "#211C2E" },
  statusSubline: { fontSize: 12, color: "#8B8398", lineHeight: 1.45, maxWidth: 380 },
  statusMeterBlock: { display: "flex", flexDirection: "column", gap: 6 },
  statusMeterLabelRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  statusMeterLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    fontFamily: "'IBM Plex Mono', monospace",
    color: "#A79FBC",
  },
  statusMeterTrack: {
    height: 6,
    borderRadius: 4,
    background: "#EFEDF6",
    overflow: "hidden",
  },
  statusMeterFill: {
    height: "100%",
    borderRadius: 4,
    transition: "width 0.25s ease, background 0.25s ease",
  },

  transcriptRow: { display: "flex", alignItems: "flex-start", gap: 10, flexShrink: 0 },
  micIndicator: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#F5F4FA",
    border: "1px solid rgba(33,28,46,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.2s ease, border-color 0.2s ease",
    marginTop: 2,
  },
  micIndicatorActive: {
    background: "#E8F6EE",
    border: "1px solid #B7E4C8",
  },
  transcriptBox: {
    flex: 1,
    background: "#F7F7FB",
    border: "1px solid rgba(33,28,46,0.06)",
    borderRadius: 12,
    padding: "8px 14px 10px",
    minHeight: 20,
  },
  transcriptLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: "#6D4FC7",
    marginBottom: 2,
  },
  transcriptText: { fontSize: 13.5, fontFamily: "'IBM Plex Mono', monospace", color: "#211C2E", lineHeight: 1.45 },
  manualInputRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
    paddingTop: 8,
    borderTop: "1px solid rgba(33,28,46,0.06)",
  },
  manualInput: {
    flex: 1,
    border: "1px solid rgba(33,28,46,0.10)",
    background: "#FFFFFF",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 13,
    fontFamily: "'Inter', sans-serif",
    color: "#211C2E",
    outline: "none",
  },
  manualSendBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: "none",
    background: "#4F6BFF",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  manualSendBtnDisabled: {
    background: "#C9D0F5",
    cursor: "default",
  },
  warnBoxInline: {
    background: "#F3ECFA",
    border: "1px solid #DCCBF0",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 12.5,
    color: "#5B3E85",
    marginBottom: 8,
  },
  tabSwitchWarnBox: {
    background: "#FBEDE7",
    border: "1px solid #E7B8A6",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 600,
    color: "#B8476B",
    marginBottom: 8,
    textAlign: "center",
  },
  inlineRetryBtn: {
    border: "none",
    background: "none",
    color: "#6D4FC7",
    fontWeight: 600,
    fontSize: 12.5,
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline",
  },
  finishedBox: {
    textAlign: "center",
    padding: "8px 0 2px",
    fontSize: 13,
    color: "#6E6580",
    fontWeight: 600,
  },
  ghostBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#F5F4FA",
    border: "1px solid rgba(33,28,46,0.08)",
    color: "#4B4459",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    position: "relative",
    marginTop: 2,
  },
  speakingDot: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#4E8B7C",
    display: "inline-block",
    animation: "mi-pulse 1s infinite ease-in-out",
  },
  warningOverlay: {
    position: "fixed",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    pointerEvents: "none",
    zIndex: 9999,
  },
  warningToast: {
    background: "rgba(184,71,107,0.95)",
    color: "#FFFFFF",
    padding: "14px 26px",
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    textAlign: "center",
    maxWidth: "80%",
    boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
    animation: "mi-toast-fade 3s ease forwards",
  },
};

if (typeof document !== "undefined" && !document.getElementById("mi-keyframes")) {
  const styleTag = document.createElement("style");
  styleTag.id = "mi-keyframes";
  styleTag.innerHTML = `
    @keyframes mi-bounce { 0%, 80%, 100% { transform: translateY(0); opacity: 0.4; } 40% { transform: translateY(-4px); opacity: 1; } }
    @keyframes mi-pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
    @keyframes mi-wave { 0%, 100% { height: 4px; } 50% { height: 16px; } }
    @keyframes mi-ring-pulse {
      0%, 100% { box-shadow: 0 0 0 4px rgba(79,107,255,0.14), 0 6px 12px rgba(33,28,46,0.14); transform: scale(1); }
      50% { box-shadow: 0 0 0 8px rgba(79,107,255,0.06), 0 6px 12px rgba(33,28,46,0.14); transform: scale(1.02); }
    }
    @keyframes mi-ring-breathe {
      0%, 100% { box-shadow: 0 0 0 3px rgba(47,143,91,0.10), 0 6px 12px rgba(33,28,46,0.14); }
      50% { box-shadow: 0 0 0 6px rgba(47,143,91,0.05), 0 6px 12px rgba(33,28,46,0.14); }
    }
    @keyframes mi-toast-fade {
      0% { opacity: 0; transform: translateY(-8px); }
      10% { opacity: 1; transform: translateY(0); }
      85% { opacity: 1; }
      100% { opacity: 0; }
    }
  `;
  document.head.appendChild(styleTag);
}